import React, { useRef } from 'react';
import { CardArt, ROOM_META } from './cards.jsx';

// Pointer-tracked tilt for the "holographic card" feel.
const useTilt = (max = 10) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--rx', `${(-py * max).toFixed(2)}deg`);
    el.style.setProperty('--ry', `${(px * max).toFixed(2)}deg`);
    el.style.setProperty('--mx', `${((px + 0.5) * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${((py + 0.5) * 100).toFixed(1)}%`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };
  return { ref, onMove, onLeave };
};

const Card = ({
  card,
  variant = 'full',
  index = 0,
  onSelect,
  dimmed,
  active,
  struck,
}) => {
  const tilt = useTilt(variant === 'full' ? 9 : 5);
  const room = ROOM_META[card.room];

  const className = [
    'stsCard',
    `variant-${variant}`,
    `type-${card.type}`,
    `rarity-${card.rarity}`,
    `room-${card.room}`,
    dimmed ? 'is-dimmed' : '',
    active ? 'is-active' : '',
    struck ? 'is-struck' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (variant === 'tile') {
    return (
      <button
        ref={tilt.ref}
        className={className}
        style={{ '--i': index }}
        onMouseMove={tilt.onMove}
        onMouseLeave={tilt.onLeave}
        onClick={() => onSelect?.(card)}
      >
        <span className="cardGlow" aria-hidden="true" />
        <span className="tileArt">
          <CardArt id={card.id} />
          <span className="tileCost">{card.cost}</span>
        </span>
        <span className="tileMeta">
          <span className="tileName">{card.name}</span>
          <span className="tileSection">{card.section}</span>
        </span>
      </button>
    );
  }

  return (
    <button
      ref={tilt.ref}
      className={className}
      style={{ '--i': index }}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      onClick={() => onSelect?.(card)}
      aria-label={`${card.name} — enter ${card.section}`}
    >
      <span className="cardGlow" aria-hidden="true" />
      <span className="cardFrame">
        <span className="cardCost">
          <span className="cardCostNum">{card.cost}</span>
        </span>
        {card.damage != null && (
          <span className="cardDamage" title="Damage">
            <span className="cardDamageIcon" aria-hidden="true">
              ⚔
            </span>
            {card.damage}
          </span>
        )}
        {struck && <span className="struckStamp">STRUCK</span>}
        <span className="cardName">{card.name}</span>
        <span className="cardArt">
          <span className="cardArtScene">
            <CardArt id={card.id} />
          </span>
          <span className="cardSheen" aria-hidden="true" />
        </span>
        <span className="cardBanner">
          <span className="rarityGem" aria-hidden="true" />
          <span className="cardBannerText">
            {card.type} · {room?.label}
          </span>
        </span>
        <span className="cardDesc">
          <span className="cardAbility">{card.ability}</span>
          <span className="cardFlavor">{card.flavor}</span>
        </span>
      </span>
    </button>
  );
};

export default Card;
